import { useLazyQuery } from '@apollo/client';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {
  Box,
  Button,
  CircularProgress,
  Link,
  List,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import type { AddressFile } from '@provision/entity-types';
import { getFullName } from '@provision/formatting-utils';
import dayjs from 'dayjs';
import { useCallback, useEffect } from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { Address, useGroupRules, UserGroupRules } from 'src/graphql';
import { GET_DTOOLS_PROJECT_DETAILS } from 'src/graphql/entities/integrations/dtools/dtools.operations';
import { DtoolsProjectDetailsResponse } from 'src/graphql/entities/integrations/dtools/dtools.types';
import { GET_PORTALIO_PROPOSAL_DETAILS } from 'src/graphql/entities/integrations/portalio/portalio.operations';
import { Modals, useShowModals } from 'src/store/modals';
import { useNotifications } from 'src/utils/hooks';
import { FileListItem } from './file-list-item';
import { FileListItemSkeleton } from './file-list-item-skeleton';
import { useAddressFileList } from './use-address-file-list';
import { getFileTypeFromContentType } from './utils';

interface Props {
  disabled?: boolean;
  address?: Partial<Address> | null;
  onAutoFillClicked?: () => void;
  isAutoFillLoading?: boolean;
  onFileCountChange?: (count: number) => void;
  title?: string;
}

type PortalioProposalDetailsQuery = {
  params: {
    proposalId: number;
  };
};

type PortalioProposalDetailsResponse = {
  getPortalioProposalDetails: {
    content: string;
  };
};

type DtoolsProjectDetailsQuery = {
  params: {
    projectId: string;
  };
};

export const AddressFilesDropzone = ({
  disabled = false,
  address,
  onAutoFillClicked,
  isAutoFillLoading,
  onFileCountChange,
  title,
}: Props) => {
  const {
    getRootProps,
    getInputProps,
    listContainerRef,
    handleScroll,
    addressFiles,
    fileCount,
    isLoadingAddressFiles,
    handleDeleteFile,
    handleFilePin,
    handleFileUnpin,
    handleDownloadFile,
    handleOpenFile,
    handleRename,
    isUploadingFiles,
    isSavingConversationAttachment,
    isConversationAttachmentDragActive,
    handleConversationAttachmentDragEnter,
    handleConversationAttachmentDragOver,
    handleConversationAttachmentDragLeave,
    handleConversationAttachmentDrop,
    handleFileUpload,
  } = useAddressFileList({
    addressId: address?.addressId ?? '',
    partnerId: address?.partnerId,
    includeDeleted: address?.isDeleted,
  });

  useEffect(() => {
    onFileCountChange?.(fileCount ?? 0);
  }, [fileCount, onFileCountChange]);

  const [showImportProposalsModal] = useShowModals(Modals.ImportProposals);

  const [getPortalioProposalDetails] = useLazyQuery<
    PortalioProposalDetailsResponse,
    PortalioProposalDetailsQuery
  >(GET_PORTALIO_PROPOSAL_DETAILS);

  const [getDtoolsProjectDetails] = useLazyQuery<
    DtoolsProjectDetailsResponse,
    DtoolsProjectDetailsQuery
  >(GET_DTOOLS_PROJECT_DETAILS);

  const { showErrorAlert } = useNotifications();

  const onPortalioProposalsSelected = useCallback(
    async (proposals: { id: number; name: string }[]) => {
      for (const proposal of proposals) {
        await getPortalioProposalDetails({
          variables: {
            params: {
              proposalId: proposal.id,
            },
          },
          fetchPolicy: 'network-only',
          onCompleted: (data) => {
            handleFileUpload([
              new File(
                [data.getPortalioProposalDetails.content],
                `portal_io - ${proposal.name?.toLowerCase()} - ${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.md`,
                { type: 'text/plain' },
              ),
            ]);
          },
          onError: (error) => {
            showErrorAlert(error.message);
          },
        });
      }
    },
    [getPortalioProposalDetails, handleFileUpload, showErrorAlert],
  );

  const onDtoolsProjectsSelected = useCallback(
    async (projects: { id: string; name: string }[]) => {
      for (const project of projects) {
        await getDtoolsProjectDetails({
          variables: {
            params: {
              projectId: project.id,
            },
          },
          fetchPolicy: 'network-only',
          onCompleted: (data) => {
            handleFileUpload([
              new File(
                [data.getDtoolsProjectDetails.content],
                `D-Tools Cloud - ${project.name?.toLowerCase()} - ${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.md`,
                { type: 'text/plain' },
              ),
            ]);
          },
          onError: (error) => {
            showErrorAlert(error.message);
          },
        });
      }
    },
    [getDtoolsProjectDetails, handleFileUpload, showErrorAlert],
  );

  const theme = useTheme();
  const dropzoneLabel = (() => {
    if (isSavingConversationAttachment) {
      return 'Saving attachment...';
    }

    if (isUploadingFiles) {
      return 'Uploading files...';
    }

    if (isConversationAttachmentDragActive) {
      return 'Drop attachment to save to site files';
    }

    return 'Drop files or conversation attachments here, or click to browse';
  })();

  const { checkValidity, loading: loadingGroupRules } = useGroupRules();
  const allowIntegrations =
    !loadingGroupRules &&
    checkValidity(
      [UserGroupRules.AccessToDtoolsImportTool, UserGroupRules.AccessToPortalioImportTool],
      { matchType: 'any' },
    );

  return (
    <Stack>
      {title ? (
        <Stack direction="row" justifyContent="space-between" mb={1}>
          <Typography variant="subtitle2" fontSize="0.75rem">
            {title}
          </Typography>
          {fileCount > 0 ? (
            <Typography fontSize="0.75rem">
              {fileCount || 0} {fileCount > 1 ? 'files' : 'file'}
            </Typography>
          ) : null}
        </Stack>
      ) : null}

      <Box
        {...getRootProps({
          onDragEnter: handleConversationAttachmentDragEnter,
          onDragOver: handleConversationAttachmentDragOver,
          onDragLeave: handleConversationAttachmentDragLeave,
          onDrop: handleConversationAttachmentDrop,
        })}
        sx={{
          border: '2px dashed',
          borderColor: isConversationAttachmentDragActive
            ? theme.palette.primary.main
            : theme.palette.divider,
          borderRadius: 0.6,
          padding: 2,
          textAlign: 'center',
          cursor: 'pointer',
          mb: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          transition: 'background-color 0.2s ease, border-color 0.2s ease',
          backgroundColor: isConversationAttachmentDragActive
            ? theme.palette.action.selected
            : 'transparent',
          '&:hover': {
            backgroundColor: (theme) =>
              isConversationAttachmentDragActive
                ? theme.palette.action.selected
                : theme.palette.action.hover,
            borderColor: (theme) =>
              isConversationAttachmentDragActive
                ? theme.palette.primary.main
                : theme.palette.text.secondary,
          },
          ...(disabled && {
            pointerEvents: 'none',
            opacity: 0.6,
          }),
          marginBottom: !allowIntegrations ? theme.spacing(2) : undefined,
        }}
      >
        <input {...getInputProps()} disabled={disabled} style={{ display: 'none' }} />

        <Typography
          component="span"
          variant="body2"
          color="text.secondary"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            lineHeight: 1.2,
            whiteSpace: 'break-spaces',
          }}
        >
          {isUploadingFiles || isSavingConversationAttachment ? (
            <CircularProgress size="1.3rem" color="inherit" />
          ) : (
            <DynamicIcon name="upload" />
          )}
          {dropzoneLabel}
        </Typography>
      </Box>
      {allowIntegrations && !disabled ? (
        <Link
          component="button"
          variant="body2"
          color="text.secondary"
          onClick={() => {
            showImportProposalsModal({
              onItemsSelected: async (items, source) => {
                if (source === 'portalio') {
                  await onPortalioProposalsSelected(items as { id: number; name: string }[]);
                }
                if (source === 'dtools') {
                  await onDtoolsProjectsSelected(items as { id: string; name: string }[]);
                }
              },
            });
          }}
          underline="hover"
          sx={{
            marginBottom: theme.spacing(2),
          }}
        >
          Import from...
        </Link>
      ) : null}

      <Box ref={listContainerRef} onScroll={handleScroll}>
        <List disablePadding>
          {isLoadingAddressFiles ? (
            <>
              <FileListItemSkeleton />
              <FileListItemSkeleton />
              <FileListItemSkeleton />
            </>
          ) : (
            addressFiles.map((addressFile: AddressFile) => (
              <FileListItem
                key={addressFile.id}
                disabled={disabled}
                fileName={addressFile.fileName}
                fileType={getFileTypeFromContentType(addressFile.contentType)}
                uploadedBy={
                  getFullName({
                    firstName: addressFile.uploadedByUser?.firstName,
                    lastName: addressFile.uploadedByUser?.lastName,
                  }) || 'Unknown'
                }
                uploadedAt={addressFile.createdAt}
                pinnedAt={addressFile.pinnedAt}
                onPin={() => handleFilePin(addressFile)}
                onUnpin={() => handleFileUnpin(addressFile)}
                onRename={(fileName: string) => handleRename(fileName, addressFile)}
                onDelete={() => handleDeleteFile(addressFile)}
                onDownload={() => handleDownloadFile(addressFile)}
                onOpen={() => handleOpenFile(addressFile)}
              />
            ))
          )}
        </List>
        {!!addressFiles.length && !!onAutoFillClicked && (
          <Button
            disabled={isAutoFillLoading}
            onClick={onAutoFillClicked}
            size="small"
            sx={{ mt: 1, p: 1, whiteSpace: 'break-spaces' }}
          >
            <AutoAwesomeIcon sx={{ marginRight: '1rem' }} fontSize="small" />
            {isAutoFillLoading ? 'Auto filling...' : 'Auto-fill site documentation'}
          </Button>
        )}
      </Box>
    </Stack>
  );
};
